import React from 'react';
import { AlertTriangle } from 'lucide-react';
import Button from '../../components/ui/Button';
import { TONE } from '../../components/ui/tokens';
import type { FixedPackWeightQuestion } from '../../lib/vendorInvoiceApi';

/**
 * ARE THESE FIXED PACKS? — asked on a catch-weight line whose Unit Size is a
 * weight. Prod USC-BUG-0028, Albrosco EINV00307996, BULK STREAKY BACON 3 x 2 KG.
 *
 * A reconciled weight grid makes a line catch-weight (`caseWeights.isCatchWeightLine`,
 * ClickUp 86d47b5cw), and a catch-weight line does not tell the Master Catalogue
 * what one pack weighs: meat "never stays constant" (Deborah, 2026-08-31). The
 * bacon's boxes were weighed, yet it comes in fixed 2 kg packs, so the client had
 * to add "1 PACK = 2 KG" by hand. Owner's decision, 2026-09-15: ask. Unanswered,
 * nothing changes and Confirm & sync is not refused; answered yes, the sync writes
 * the pack weight.
 *
 * WHICH lines are asked is decided on the server, by the owners of both halves —
 * `isCatchWeightLine` and `pricingBasis.getUnitCategory` — neither of which has a
 * client twin, and a copy here is how a unit comes to be classified two ways. The
 * server sends the questions with the invoice (`fixedPackWeightQuestions`); this
 * file only decides whether a question still describes the line on screen, and
 * says it.
 */

/**
 * The question to put on this line NOW, or null.
 *
 * The server worked its questions out from the invoice as saved. The line on
 * screen can have moved since — answered, or its Unit Size retyped or re-read —
 * and a question about text that is no longer there must not be asked. The grid
 * reloads after saving such an edit (`unitSizeQuestionsMayHaveChanged`), so the
 * gap lasts until Save.
 */
export function pendingFixedPackWeightQuestion(
  line: any,
  index: number,
  questions: FixedPackWeightQuestion[] | null | undefined
): FixedPackWeightQuestion | null {
  const question = (Array.isArray(questions) ? questions : []).find((q) => q.index === index);
  if (!question) return null;
  if (line?.splitUnitSizeFixed === true) return null;
  const unitSize = line?.splitUnitSize == null ? null : String(line.splitUnitSize);
  if (unitSize !== question.unitSize) return null;
  if ((line?.splitUnitSizeReading || null) !== question.reading) return null;
  return question;
}

export interface FixedPackWeightPromptProps {
  question: FixedPackWeightQuestion;
  lineNo: number;
  /** View mode: the question is shown, and cannot be answered from here. */
  readOnly?: boolean;
  onAnswer: () => void;
}

export function FixedPackWeightPrompt({ question, lineNo, readOnly = false, onAnswer }: FixedPackWeightPromptProps) {
  const ask = `Sold by weight, so ${question.size} won't be saved to the catalogue. Are these fixed ${question.size} packs?`;

  return (
    <div
      role="group"
      aria-label={`Line ${lineNo}: are these fixed ${question.size} packs`}
      className={`sticky left-0 flex w-fit max-w-3xl flex-wrap items-center gap-2 rounded-md border px-3 py-2 text-xs ${TONE.attention}`}
    >
      <AlertTriangle aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
      <span>{`Line ${lineNo}: ${ask}`}</span>
      <Button size="sm" disabled={readOnly} onClick={onAnswer}>
        {`Yes — fixed ${question.size} packs`}
      </Button>
    </div>
  );
}

export default FixedPackWeightPrompt;
